import { EditableText, defineBlock } from "eddev/blocks"
import { cn } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef } from "react"
import { Col, Container, Grid } from "@components/layout/Grid"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/atoms/Button"
import { ButtonList } from "@components/atoms/ButtonList"
import { Heading } from "@components/atoms/Heading"
import { Typography } from "@components/atoms/Typography"
import { Chip } from "@components/atoms/Chip"
import { VSpace } from "@components/layout/Spacer"
import { useIsMobile } from "@hooks/useViewportDerived"

export const meta: BlockMeta = {
  title: "Page headers",
  category: "common",
  icon: "align-pull-right",
  keywords: ["header"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
    multiple: false,
  },
}

export default defineBlock("general/header-with-links", (props) => {
  const isMobile = useIsMobile()

  let showWarning = false

  // Show a warning if this block isn't placed at the top of a page
  if (env.admin) {
    const wp = (window as any).wp
    showWarning = wp.data.useSelect((select: any) => {
      const blocks = select("core/block-editor").getBlocks()
      return blocks[0]["name"] !== "acf/general-header-with-links"
    })
  }

  return (
    <>
      {env.admin && showWarning && <ErrorMessage>This block should be placed at the top of the page</ErrorMessage>}
      {props.useFullWidthImage ? (
        <div className="px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] mx-auto">
          <div className="grid-auto !w-full gap-6 my-7">
            <div className="col-span-12">
              <ResponsiveImage {...props.heroImage} aspect={1136 / 240} className="overflow-hidden rounded-lg" />
            </div>
          </div>

          <div className="grid-auto gap-6">
            {!isMobile && !env.admin && <Col initial={12} tablet={1} laptop={2} />}
            <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
              <VSpace initial={5} />
              <Chip color="grey">
                <EditableText
                  store="Chip"
                  placeholder={props.page?.title ?? ""}
                  defaultValue={props.page?.title ?? ""}
                />
              </Chip>
              <VSpace initial={4} />
              <EditableText as="h1" store="Heading" placeholder="Enter heading..." />
              <VSpace initial={4} />
            </div>
            {!isMobile && !env.admin && <Col initial={12} tablet={1} laptop={2} />}
          </div>
        </div>
      ) : (
        <div className="px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] mx-auto">
          <div className="grid-auto !w-full gap-6 bg-light-grey rounded-md my-7 overflow-hidden [&_h1]:type-title-xl [&_h2]:type-title-xl lg:justify-between wp:lg:w-full">
            <StretchCol initial={12} tablet={6} laptop={7} className="order-1 md:order-2 relative">
              <ResponsiveImage {...props.heroImage} objectFit="cover" className="absolute top-0 left-0 w-full h-full" />
            </StretchCol>
            <Col initial={12} tablet={6} laptop={5} className="order-2 md:order-1 md:flex md:flex-col">
              <ContentContainer>
                <Heading as="h1">{props.page?.title}</Heading>

                {props.useText ? (
                  <TextContainer>
                    <Typography as="p" dangerouslySetInnerHTML={{ __html: props.text ?? "" }} />
                  </TextContainer>
                ) : (
                  <>
                    {props.linkList && (
                      <ButtonList>
                        {props.linkList.map((link, i) => (
                          <Button
                            color={link?.buttonStyle === "regular" ? "grey" : "yellow"}
                            size="small"
                            icon={link?.link?.target ? "ARROW_UP_RIGHT" : "ARROW_RIGHT"}
                            iconStyle="right"
                            href={link?.link?.url || ""}
                            target={link?.link?.target ? "_blank" : undefined}
                            key={i}
                            onClick={(e) => {
                              const url = link?.link?.url ?? ""
                              if (url.includes("#")) {
                                const element = document.getElementById(url.split("#")[1])
                                if (element) {
                                  e.preventDefault()
                                  scrollTo({
                                    top: element.offsetTop - 100,
                                    behavior: "smooth",
                                  })
                                }
                              }
                            }}
                          >
                            {link?.link?.title}
                          </Button>
                        ))}
                      </ButtonList>
                    )}
                  </>
                )}
              </ContentContainer>
            </Col>
          </div>
        </div>
      )}
    </>
  )
})

const StyledGrid = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Grid>>(({ className, ...props }, ref) => (
  <Grid
    ref={ref}
    className={cn(
      "bg-light-grey rounded-md my-7 overflow-hidden [&_h2]:type-title-xl lg:justify-between wp:lg:w-full",
      className,
    )}
    {...props}
  />
))
StyledGrid.displayName = "StyledGrid"

// `[&>div]:pt-0 [&>div]:h-full [&_img]:relative` overrides ResponsiveImage's
// own aspect-ratio box so the image fills this fixed-height column instead —
// same override already established in get-involved.tsx for an identical
// fixed-height/`objectFit="cover"` column.
const StretchCol = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Col>>(({ className, ...props }, ref) => (
  <Col
    ref={ref}
    className={cn(
      "h-[calc(var(--tw-grid-inner-width)*(194/344))] min-h-full max-h-[280px] overflow-hidden",
      "[&>div]:pt-0 [&>div]:h-full [&_img]:relative",
      "lg:max-h-[380px]",
      className,
    )}
    {...props}
  />
))
StretchCol.displayName = "StretchCol"

const FullCol = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Col>>(({ className, ...props }, ref) => (
  <Col ref={ref} className={cn("!w-full rounded-md overflow-hidden", className)} {...props} />
))
FullCol.displayName = "FullCol"

const ContentContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col justify-between p-5 w-full grow md:pt-6 md:pr-0 md:pb-6 md:pl-7", className)}
    {...props}
  />
))
ContentContainer.displayName = "ContentContainer"

const TextContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("mt-8", className)} {...props} />
))
TextContainer.displayName = "TextContainer"

const ErrorMessage = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-red bg-[rgba(255,0,0,0.2)] border border-red text-center p-[14px]", className)}
    {...props}
  />
))
ErrorMessage.displayName = "ErrorMessage"
